import React, { useEffect, useState } from "react";
import { X, Plus } from "lucide-react";
import { SectionHeading, FieldLabel, Chip, StepInstructions } from "@/components/wizard/ui";
import { FieldInfo } from "@/components/wizard/InfoTip";
import { DIET_PATTERNS, COOKING_STYLES } from "@/lib/planModel";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const inputCls = "h-11 rounded-none bg-[#141414] border-white/10 focus-visible:ring-[#004AAD]";

function TagList({ items, onRemove, testPrefix, color = "#FF3B30" }) {
  if (!items.length) return <p className="text-xs text-zinc-200">None added yet.</p>;
  return (
    <div className="flex flex-wrap gap-2">
      {items.map((it) => (
        <span key={it} data-testid={`${testPrefix}-${it}`}
          className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs border bg-[#141414]"
          style={{ borderColor: color + "66", color }}>
          {it}
          <button onClick={() => onRemove(it)} className="hover:opacity-70" data-testid={`${testPrefix}-remove-${it}`}>
            <X className="h-3 w-3" />
          </button>
        </span>
      ))}
    </div>
  );
}

export default function Step2Dietary({ plan, setPlan }) {
  const { dietary } = plan;
  // Only `true` (explicit YES) reveals dietary details. `null` (undecided,
  // new plans) and `false` both keep the detail sections hidden — YES is the
  // switch that unlocks them.
  const wantsMealPlanning = plan.mealPlanning === true;
  // Any dietary change invalidates the generated meal plan so it must be rebuilt
  // in Step 5 with the new exclusions (prevents a stale plan ignoring changes).
  const patchDiet = (updates) => setPlan((p) => ({
    ...p,
    dietary: { ...p.dietary, ...updates },
    generated: null,
    selections: {},
  }));
  const setMealPlanning = (value) => setPlan((p) => ({
    ...p,
    mealPlanning: value,
    // Flipping the toggle invalidates the generated plan so users get the
    // right output on the next regenerate.
    generated: null,
    selections: {},
  }));
  const [groups, setGroups] = useState([]);
  const [approvedSuggestions, setApprovedSuggestions] = useState([]);
  const [customEx, setCustomEx] = useState("");
  const [approved, setApproved] = useState("");

  useEffect(() => {
    api.get("/meta/exclusions").then(({ data }) => {
      setGroups(data.groups || (data.common ? [{ label: "Common", items: data.common }] : []));
      setApprovedSuggestions(data.approvedProducts || []);
    }).catch(() => {});
  }, []);

  const allExclusions = [...dietary.exclusions, ...dietary.customExclusions];

  const toggleCommon = (term) => {
    const has = dietary.exclusions.includes(term);
    patchDiet({ exclusions: has ? dietary.exclusions.filter((t) => t !== term) : [...dietary.exclusions, term] });
  };
  const addCustom = () => {
    const v = customEx.trim().toLowerCase();
    if (v && !allExclusions.includes(v)) patchDiet({ customExclusions: [...dietary.customExclusions, v] });
    setCustomEx("");
  };
  const removeCustom = (v) => patchDiet({ customExclusions: dietary.customExclusions.filter((t) => t !== v) });
  const addApproved = () => {
    const v = approved.trim();
    if (v && !dietary.approvedProducts.includes(v)) patchDiet({ approvedProducts: [...dietary.approvedProducts, v] });
    setApproved("");
  };
  const removeApproved = (v) => patchDiet({ approvedProducts: dietary.approvedProducts.filter((t) => t !== v) });

  const excludedCount = dietary.exclusions.length + dietary.customExclusions.length;

  return (
    <div>
      <SectionHeading eyebrow="Step 03" title="Dietary Requirements"
        desc="Exclusions filter the meal library so every pathway respects your allergens, intolerances, triggers and preferences." />

      <StepInstructions>
        Confirm your dietary pattern, then tap any foods to exclude (allergens, intolerances or dislikes). Add anything not listed as a custom exclusion, and list the sports products you already trust so we can safely include them.
      </StepInstructions>

      <div className="space-y-10 max-w-3xl">
        <div>
          <FieldLabel info={
            <FieldInfo testId="info-diet-pattern" title="Dietary pattern">
              Sets which foods every meal pathway draws from. Omnivore uses all foods; Pescatarian keeps fish but no other meat; Vegetarian excludes all meat and fish; Vegan excludes all animal products. You can still fine-tune with exclusions below.
            </FieldInfo>
          }>Dietary pattern</FieldLabel>
          <div className="flex flex-wrap gap-2">
            {DIET_PATTERNS.map((d) => (
              <Chip key={d.value} testId={`dietary-pattern-${d.value}`} active={dietary.pattern === d.value}
                onClick={() => patchDiet({ pattern: d.value })}>{d.label}</Chip>
            ))}
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-3">
            <FieldLabel>Exclusions — tap anything to remove it from your plan</FieldLabel>
            <span className="text-[11px] text-[#E2E30A] uppercase tracking-wider" data-testid="exclusion-count">{excludedCount} excluded</span>
          </div>
          <div className="space-y-5">
            {groups.map((g) => (
              <div key={g.label} data-testid={`exclusion-group-${g.label}`}>
                <p className="text-[11px] uppercase tracking-[0.2em] text-zinc-200 mb-2">{g.label}</p>
                <div className="flex flex-wrap gap-2">
                  {g.items.map((c) => (
                    <Chip key={c} testId={`dietary-exclusion-${c}`} active={dietary.exclusions.includes(c)}
                      onClick={() => toggleCommon(c)}>{c}</Chip>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <FieldLabel>Custom exclusions (anything not listed above)</FieldLabel>
          <div className="flex gap-2 mb-3">
            <Input data-testid="dietary-custom-input" className={inputCls} value={customEx}
              onChange={(e) => setCustomEx(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCustom())}
              placeholder="e.g. mushrooms, mango, kiwi" />
            <Button data-testid="dietary-custom-add-btn" onClick={addCustom}
              className="rounded-none bg-white text-black hover:bg-zinc-200 transition-colors"><Plus className="h-4 w-4" /></Button>
          </div>
          <TagList items={dietary.customExclusions} onRemove={removeCustom} testPrefix="dietary-custom" />
        </div>

        <div>
          <FieldLabel>Approved sports products (safe to include)</FieldLabel>
          <div className="flex gap-2 mb-3">
            <Input data-testid="dietary-approved-input" className={inputCls} value={approved}
              onChange={(e) => setApproved(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addApproved())}
              placeholder="e.g. Maurten Gel 100" list="approved-suggestions" />
            <datalist id="approved-suggestions">
              {approvedSuggestions.map((p) => <option key={p} value={p} />)}
            </datalist>
            <Button data-testid="dietary-approved-add-btn" onClick={addApproved}
              className="rounded-none bg-white text-black hover:bg-zinc-200 transition-colors"><Plus className="h-4 w-4" /></Button>
          </div>
          <TagList items={dietary.approvedProducts} onRemove={removeApproved} testPrefix="dietary-approved" color="#34C759" />
        </div>
      </div>
    </div>
  );
}
